import { useState } from 'react';
import { formatJMD } from '../../lib/finances';
import { invoiceTotal, lineTotal } from '../../lib/invoices';
import { todayISO } from '../../lib/dates';
import ProductAutocomplete from './ProductAutocomplete';
import StockistPicker from './StockistPicker';

const BLANK_ITEM = { description: '', product_id: null, unit_price: '', quantity: '' };

// Sending stock out on consignment.
//
// Deliberately not the direct invoice form with a toggle: the two ask
// different questions. There is no "due date" here, because nothing is due
// until something sells — the money arrives month by month afterwards. What
// there is instead is the shop's profile and a stock check, since the thing
// you can actually get wrong is sending more than you have.
export default function StockistInvoiceForm({
  products,
  stockists,
  invoices,
  statementsByInvoice,
  onCreateStockist,
  onSubmit,
  onCancel,
}) {
  const [stockistId, setStockistId] = useState(null);
  const [issueDate, setIssueDate] = useState(todayISO());
  const [notes, setNotes] = useState('');
  const [items, setItems] = useState([{ ...BLANK_ITEM }]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const setItem = (index, patch) =>
    setItems((rows) => rows.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  const addRow = () => setItems((rows) => [...rows, { ...BLANK_ITEM }]);
  const removeRow = (index) =>
    setItems((rows) => (rows.length === 1 ? [{ ...BLANK_ITEM }] : rows.filter((_, i) => i !== index)));

  const filled = items.filter((i) => i.description.trim() && Number(i.quantity) > 0);
  const total = invoiceTotal(filled);
  const stockist = stockists.find((s) => s.id === stockistId);

  // Warned about, not blocked — the shelf count is corrected by hand and is
  // sometimes simply behind. Refusing the consignment because a number in the
  // app disagrees would be the app overruling the physical stock.
  const shortages = filled
    .map((item) => {
      const product = products.find((p) => p.id === item.product_id);
      if (!product) return null;
      const have = Number(product.stock_qty) || 0;
      const want = Number(item.quantity) || 0;
      return want > have ? { name: product.name, have, want } : null;
    })
    .filter(Boolean);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stockistId) {
      setError('Choose which shop this stock is going to.');
      return;
    }
    if (filled.length === 0) {
      setError('Add at least one product, with how many you are sending.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await onSubmit(
        {
          stockist_id: stockistId,
          // Snapshotted so the invoice still reads correctly if the shop is
          // later renamed or archived.
          bill_to_name: stockist.name,
          bill_to_address: stockist.address || null,
          bill_to_email: stockist.email || null,
          issue_date: issueDate,
          currency: 'JMD',
          exchange_rate: 1,
          channel: 'Wholesale',
          notes: notes.trim() || null,
        },
        filled
      );
    } catch (err) {
      setError(err.message);
      setSubmitting(false);
    }
  };

  return (
    <form className="invoice-form" onSubmit={handleSubmit}>
      <section className="form-block">
        <h3>Which shop?</h3>
        <StockistPicker
          stockists={stockists}
          value={stockistId}
          onChange={setStockistId}
          invoices={invoices}
          statementsByInvoice={statementsByInvoice}
          onCreate={onCreateStockist}
        />
      </section>

      <section className="form-block">
        <h3>What are you sending?</h3>
        <div className="invoice-lines">
          <div className="stockist-lines-head">
            <span>Product</span>
            <span>Qty sent</span>
            <span>Unit price</span>
            <span>Value</span>
            <span />
          </div>

          {items.map((item, index) => {
            const product = products.find((p) => p.id === item.product_id);
            return (
              <div className="stockist-line" key={index}>
                <ProductAutocomplete
                  value={item.description}
                  products={products}
                  placeholder="Pick from your products"
                  onChange={(description) => setItem(index, { description, product_id: null })}
                  onPick={(picked) =>
                    setItem(index, {
                      description: picked.name,
                      product_id: picked.id,
                      unit_price: item.unit_price || (picked.price_jmd ?? ''),
                    })
                  }
                />
                <div className="stockist-line-qty">
                  <input
                    type="number"
                    min="0"
                    step="1"
                    value={item.quantity}
                    onChange={(e) => setItem(index, { quantity: e.target.value })}
                    aria-label={`Quantity sent, line ${index + 1}`}
                  />
                  {product && <em>{product.stock_qty} in stock</em>}
                </div>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={item.unit_price}
                  onChange={(e) => setItem(index, { unit_price: e.target.value })}
                  aria-label={`Unit price, line ${index + 1}`}
                />
                <span className="invoice-line-total">{formatJMD(lineTotal(item))}</span>
                <button
                  type="button"
                  className="invoice-line-remove"
                  onClick={() => removeRow(index)}
                  aria-label={`Remove line ${index + 1}`}
                >
                  ×
                </button>
              </div>
            );
          })}

          <button type="button" className="ghost-button" onClick={addRow}>
            + Add product
          </button>
        </div>

        {shortages.length > 0 && (
          <p className="stockist-shortage">
            You're sending more than your count shows for{' '}
            {shortages.map((s) => `${s.name} (${s.want} of ${s.have})`).join(', ')}. That's fine if
            the shelf says otherwise — the count will land on zero rather than go negative.
          </p>
        )}
      </section>

      <div className="invoice-form-grid">
        <label className="field">
          <span>Date sent</span>
          <input type="date" value={issueDate} onChange={(e) => setIssueDate(e.target.value)} />
        </label>
        <label className="field">
          <span>Notes</span>
          <input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Optional" />
        </label>
      </div>

      <div className="invoice-form-footer">
        <div className="invoice-form-total">
          <span>Stock value going out</span>
          <strong>{formatJMD(total)}</strong>
          <span className="invoice-form-converted">
            Not income — this is inventory until it sells.
          </span>
        </div>
        <div className="invoice-form-actions">
          <button type="button" className="ghost-button" onClick={onCancel}>
            Cancel
          </button>
          <button type="submit" className="primary-action" disabled={submitting}>
            {submitting ? 'Sending…' : 'Send stock'}
          </button>
        </div>
      </div>

      {error && <p className="po-error">{error}</p>}
    </form>
  );
}
